/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action, computed } from '@ember/object';
import { isEmpty, isNone } from '@ember/utils';
import { task } from 'ember-concurrency';
import { isArray } from '@ember/array';
import AccountService from 'cio/services/account';
import AlertsService from 'cio/services/alerts';
import AuthService from 'cio/services/auth';
import ApiService, { ApiError, RequestMethod } from 'cio/services/api';
import EnvironmentService from 'cio/services/environment';
import PushService from 'cio/services/push';
import AnalyticsService from 'cio/services/analytics';
import ActionModel from 'cio/models/action';
import TemplateModel from 'cio/models/template';
import LogModel from 'cio/models/log';
import CampaignModel from 'cio/models/campaign';
import CustomerModel from 'cio/models/customer';
import { EventsType } from 'cio/utils/analytics/events';
import { ComposerEditableEntityAnalyticsData } from 'cio/utils/composer/composer-editable-entity';
import { Platform } from 'cio/utils/push/platforms';
import { EMPTY_BODY_METHODS } from 'cio/utils/composer/webhooks';
import getPreviewVariables from 'cio/utils/get-preview-variables';
import { prettyJSONStringify } from 'cio/utils/objects';
import { PreviewVariablesArgs } from 'cio/utils/get-preview-variables';

const TYPE_LABELS: Record<string, string> = {
  email: 'email',
  twilio: 'message',
  webhook: 'webhook',
  slack: 'Slack message',
  push: 'push',
  urban_airship: 'Urban Airship',
  in_app: 'in-app message',
};

export const RECIPIENT_FIELD: Record<string, string> = {
  email: 'email address',
  twilio: 'phone number',
  slack: 'channel or username',
  push: 'token',
  urban_airship: 'named user',
  in_app: 'token',
};

type Data = {
  [key: string]: any;
};

type PushProvider = {
  provider: string;
  platform: string;
  allowDevelopment: boolean;
  development: boolean;
  key: string;
};

interface CioSendTestContentArgs {
  action?: ActionModel;
  template?: TemplateModel;
  preview: Record<string, any>;
  preprocessor?: 'none' | 'premailer';
  isDisabled?: boolean;
  placeholder?: string;
  type: string;
  requestMethod?: RequestMethod;
  responseAttributes?: Record<string, any>;
  recipient?: string;
  isRecipientReadOnly?: boolean;
  prependTest?: boolean;
  platform?: Platform;
  selectedEvent?: LogModel;
  selectedCustomer?: CustomerModel;
  analyticsData?: ComposerEditableEntityAnalyticsData & Data;
  showErrorsOnInput?: boolean;
  forceSelectedPlatform?: PushProvider;
  useCredentials?: boolean;
  onPlatformChange?(platform: Platform): unknown;
  onClose?(): unknown;
  onErrors?(errors: ApiError[]): unknown;
  getCredentials?(): unknown;
  onRecipientChange?(recipient: string): unknown;
}

export default class CioSendTestContent extends Component<CioSendTestContentArgs> {
  @service account!: AccountService;
  @service alerts!: AlertsService;
  @service api!: ApiService;
  @service auth!: AuthService;
  @service environment!: EnvironmentService;
  @service push!: PushService;
  @service analytics!: AnalyticsService;

  @tracked errors: ApiError[] = [];
  @tracked failedToSend = false;
  @tracked lastTestedRecipientBaseLSKey = 'cio:last-send-test-recipient';
  @tracked lastTestedRecipient!: Record<string, any> | null;
  @tracked recipient!: Record<string, string> | string | null;
  @tracked platform!: Platform | null;
  @tracked sandboxCheckboxDisabled = false;
  @tracked sendToSandbox = false;
  @tracked webhookResponse: Record<string, any> | null = null;
  @tracked prependTest!: boolean;

  constructor(owner: unknown, args: CioSendTestContentArgs) {
    super(owner, args);

    this.onPushPlatformsChange();
    this.recipient = this.args.recipient ?? null;
    this.platform = this.args.platform ?? null;
    this.prependTest = this.args.prependTest ?? false;
    this.lastTestedRecipient = this._getLastTestedRecipient();
  }

  @action
  onErrors(errors: ApiError[]) {
    this.errors = errors;

    this.args.onErrors?.(errors);

    if (!isEmpty(errors)) {
      this.track('send_test_error');
    }
  }

  get hasLastTestedRecipient() {
    return !isEmpty(this.lastTestedRecipient);
  }

  get showErrorsOnInput() {
    return this.args.showErrorsOnInput ?? true;
  }

  @computed('push.platforms')
  get hasMultiplePlatforms() {
    return this.push.platforms.length > 1;
  }

  @computed('platform', 'push.platforms')
  get selectedPlatform(): PushProvider | undefined {
    return (
      this.args.forceSelectedPlatform ??
      (this.push.platforms.find((item) => item.key === this.platform) as PushProvider)
    );
  }

  get isPush() {
    return this.args.type === 'push';
  }

  @action
  onPushPlatformsChange() {
    let { platforms } = this.push;

    if (this.isPush && !isEmpty(platforms)) {
      // if only one platform, use that by default for sending tests
      if (platforms.length === 1) {
        this.args.onPlatformChange?.(platforms[0].key);
      }

      // controls sandbox checkbox state
      if ((platforms as PushProvider[]).some((platform) => platform.development)) {
        this.sandboxCheckboxDisabled = true;
        this.sendToSandbox = true;
      } else {
        this.sandboxCheckboxDisabled = false;
        this.sendToSandbox = false;
      }
    }
  }

  @computed('args.responseAttributes', 'webhookResponse.{render_attributes,render_attributes_errors}')
  get _webhookResponseAttributesFormattedForDisplay() {
    return Object.keys(this.args.responseAttributes ?? {}).map((name) => {
      let newValue = this.webhookResponse?.render_attributes?.[name];
      let error = this.webhookResponse?.render_attributes_errors?.[name];

      return { name, newValue, error };
    });
  }

  @action
  track(event: keyof EventsType['message']) {
    try {
      this.tryToTrack(event);
    } catch {
      // Tracking calls aren't crucial to the app's functioning, so we can safely ignore any errors.
      // This component is used in many different scenarios, playing safe in case something unexpected
      // happen when trying to track.
    }
  }

  @action
  tryToTrack(event: keyof EventsType['message']) {
    if (this.args.analyticsData == null) {
      return;
    }
    let data = { ...this.args.analyticsData, errorMessage: '' };
    if (!isEmpty(this.errors)) {
      // passing just first error to make filtering easier
      data.errorMessage = this.errors.map((e) => JSON.stringify(e))[0];
    }
    this.analytics.track('message', event, data);
  }

  @action
  _getLastTestedRecipient() {
    let lastTestedRecipient = localStorage.getItem(this._lastTestedRecipientLSKey());
    if (isEmpty(lastTestedRecipient)) {
      return null;
    }

    try {
      return JSON.parse(lastTestedRecipient as string);
    } catch (err) {
      return null;
    }
  }

  @action
  _setLastTestedRecipient(recipient: Record<string, any>) {
    localStorage.setItem(this._lastTestedRecipientLSKey(), JSON.stringify(recipient));
    this.lastTestedRecipient = recipient;
  }

  @action
  _lastTestedRecipientLSKey() {
    let baseKey = this.lastTestedRecipientBaseLSKey;
    let { type } = this.args;
    let environmentId = this.environment.current.id;

    return `${baseKey}:${type}:env-${environmentId}`;
  }

  get _getWebhookResponseBody() {
    try {
      let responseBody = JSON.parse(this.webhookResponse?.body);
      return prettyJSONStringify(responseBody);
    } catch (err) {
      return null;
    }
  }

  get typeLabel() {
    return TYPE_LABELS[this.args.type] ?? 'message';
  }

  get showInAppPersistentBanner() {
    return this.args.type === 'in_app' && this.args.preview?.body_json?.properties?.gist?.persistent;
  }

  @computed('errors')
  get splitErrors() {
    let { errors } = this;
    let splitErrors = { recipientErrors: [] as ApiError[], nonRecipientErrors: [] as ApiError[] };

    if (isArray(errors)) {
      errors.forEach((error) => {
        if (error.source?.pointer === '/data/attributes/recipient') {
          splitErrors.recipientErrors.push(error);
        } else {
          splitErrors.nonRecipientErrors.push(error);
        }
      });
    }

    return splitErrors;
  }

  @computed('splitErrors.recipientErrors', 'args.type', 'hasMultiplePlatforms')
  get recipientErrors() {
    let { recipientErrors } = this.splitErrors;
    if (!isEmpty(recipientErrors)) {
      if (this.args.type === 'push' && this.hasMultiplePlatforms) {
        return 'Invalid device type or token';
      }

      return [`Invalid ${RECIPIENT_FIELD[this.args.type]}`];
    }
    return null;
  }

  sendTest = task({ drop: true }, async () => {
    let { preview, type, template } = this.args;
    let data: Record<string, any> = {
      from: preview.from,
      body: preview.body,
      headers: preview.headers,
      image_url: preview.image_url,
    };

    if (type === 'email' && !isEmpty(preview.body_amp)) {
      data.body_amp = preview.body_amp;
    }

    let action = this.args.action ?? template?.action;
    let campaign = action?.campaign;

    data.to = typeof this.recipient === 'string' ? (this.recipient as string)?.trim() : this.recipient?.id;

    if (action != null) {
      data.tracked = action.tracked;
    }
    if (campaign?.isEventTriggered === true && this.args.selectedEvent) {
      data.event_data = this.args.selectedEvent.attrs;
    }

    data = {
      ...data,
      ...this.prepareToSend(preview, campaign),
    };

    this.onErrors([]);
    this.failedToSend = false;

    try {
      let response = await this.api.post(`verify/${type}`, { data, ignoreErrors: true });
      let lastTestedRecipient = data.to;

      if (type === 'webhook') {
        this.webhookResponse = response;
        this.onErrors(response.errors);
      } else if (type === 'push') {
        lastTestedRecipient = {
          id: data.to,
          platform: this.platform,
        };
        if (data.sandbox === true) {
          lastTestedRecipient.sandbox = data.sandbox;
        }
      }

      if (lastTestedRecipient != null) {
        this._setLastTestedRecipient(lastTestedRecipient);
      }

      this.track('send_test');

      return response;
    } catch (e) {
      this.onErrors(e.errors);
      this.failedToSend = true;
    }
  });

  @action
  prepareToSend(preview: Record<string, any>, campaign: CampaignModel | undefined) {
    let { type } = this.args;
    if (type === 'email') {
      return this.prepareEmailToSend(preview, campaign);
    } else if (type === 'webhook') {
      return this.prepareWebhookToSend(preview, campaign);
    } else if (type === 'push') {
      return this.preparePushToSend(preview);
    } else if (type === 'twilio') {
      return this.prepareTwilioToSend();
    } else if (type === 'in_app') {
      return this.prepareInAppToSend(preview);
    }
    return {};
  }

  @action
  prepareEmailToSend(preview: Record<string, any>, campaign: CampaignModel | undefined) {
    let data: Record<string, any> = {};
    data.preprocessor = this.args.preprocessor;
    data.reply_to = preview.reply_to;
    data.subject = preview.subject;
    data.preheader_text = preview.preheader_text;
    data.prepend_test = this.prependTest;

    if (this.args.template?.newsletter != null) {
      data.campaign_type = 'newsletter';
    } else if (this.args.template?.transactionalMessage != null) {
      data.campaign_type = 'transactional_message';
    } else if (campaign != null) {
      data.campaign_type = campaign.type;
    }
    return data;
  }

  @action
  prepareWebhookToSend(preview: Record<string, any>, campaign: CampaignModel | undefined) {
    let data: Record<string, any> = {};
    let { requestMethod, responseAttributes } = this.args;
    data.request_method = requestMethod;
    data.url = preview.url;
    data.headers = preview.headers;
    data.variables = getPreviewVariables({
      customer: this.args.selectedCustomer?.attrs ?? {},
      selectedEvent: this.args.selectedEvent,
      campaign,
    } as PreviewVariablesArgs);
    if (responseAttributes != null) {
      data.response_attributes = responseAttributes;
    }
    if (requestMethod != null && EMPTY_BODY_METHODS.includes(requestMethod)) {
      data.body = null;
    }
    return data;
  }

  @action
  preparePushToSend(preview: Record<string, any>) {
    let data: Record<string, any> = {};
    data.provider = this.selectedPlatform?.provider;
    if (this.selectedPlatform?.allowDevelopment === true && this.sendToSandbox) {
      data.sandbox = true;
    }
    let hasBodyJson = preview.has_json;
    let isRich = preview.is_rich;

    data.platform = this.selectedPlatform?.key;
    data.sound = preview.sound;
    if (hasBodyJson === true) {
      data.use_json = true;
    } else if (isRich === true) {
      data.is_rich = true;
      data.title = preview.title;
      data.custom_data = preview.custom_data;
      data.link = preview.link;
    } else {
      data.title = preview.title;
    }

    if (this.args.useCredentials === true) {
      data.credentials = this.args.getCredentials?.();
    }
    return data;
  }

  prepareTwilioToSend() {
    return { whatsapp: this.args.template?.whatsapp ?? false };
  }

  prepareInAppToSend(preview: Record<string, any>) {
    let { body_json, editor } = preview;
    return { body_json, editor };
  }

  @action
  handleSubmit(event?: SubmitEvent) {
    if (event != null) {
      event.preventDefault();
    }

    this.sendTest.perform();
  }

  @action
  onLastTestedRecipientChanged(checked: boolean) {
    let recipient: Record<string, any> | null = null;

    if (checked) {
      recipient = this.lastTestedRecipient;
    }

    if (this.args.type === 'push') {
      let platform = recipient?.platform ?? null;

      if (this.hasMultiplePlatforms) {
        this.platform = platform;
        this.args.onPlatformChange?.(platform);
      }

      if (recipient && !isNone(recipient.sandbox)) {
        this.sendToSandbox = recipient.sandbox;
      }

      recipient = recipient?.id ?? null;
    }

    this.recipient = recipient;
    this.args.onRecipientChange?.(recipient?.id ?? '');
  }

  @action
  onPushPlatformSelect(platform: Platform) {
    this.platform = platform;
    this.args.onPlatformChange?.(platform);
  }

  @action
  onRecipientInput(recipient: string | null) {
    this.recipient = recipient;
    this.args.onRecipientChange?.(recipient ?? '');
  }
}
